import {
  createContext,
  PropsWithChildren,
  useCallback,
  useContext,
} from 'react';

import { AccountModel, NetworkMajorVersion } from '@block-explorer/entities';
import * as Sentry from '@sentry/react';

// @ts-ignore // TODO: Fix import in the future
import {
  Deploy,
  PublicKey as CLPublicKey,
  Transaction,
  TransactionV1,
} from 'casper-js-sdk';
import { useGetCasperNetworkService } from '../casper-service';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from 'typesafe-actions';
import { setActiveWalletAndKey, WalletType } from '../../redux/wallet';
import { useCsprClickService } from '../cspr-click-service';
import { useTranslation } from 'react-i18next';
import { CaptureContext } from '@sentry/core/build/types/types-hoist/scope';
import { ExclusiveEventHintOrCaptureContext } from '@sentry/core/build/types/utils/prepareEvent';
import { useGetCasperNetwork } from '../casper-network-context';
import { PutDeployResult, PutTransactionResult } from 'casper-js-sdk/dist/rpc';

export const WalletServiceError = {
  CANCELLED_SIGN: 'signature_cancelled',
};

type WalletService = {
  isLoading?: boolean;
  error?: string | null;
  isSignedIn: boolean;
  publicKey: string | null;
  accountHash: string | null;
  disconnect: () => void;
  connect: () => void;
  buyCSPR: () => void;
  supportTransactions: () => Promise<boolean | undefined>;
  signTransactionObj: (
    transaction: Transaction,
    accountPublicKey: string,
    isDeployV1?: boolean
  ) => Promise<any>;
  signAndSendTransaction: (
    transaction: Transaction,
    accountPublicKey: string,
    isDeployV1?: boolean
  ) => Promise<any>;
  signMessage: (message: string, accountPublicKey: string) => Promise<any>;
  activeWalletSignInLabel: string;
  activeWalletTypeLabel: string;
  connectReadOnly: (publicKey: string) => void;
  readOnlyIsConnected: boolean;
  ledgerIsConnected: boolean;
};

export const walletServiceContext = createContext<WalletService>(
  {} as WalletService
);

const { Provider: WalletServiceContextProviderV2 } = walletServiceContext;

export const useWalletService = () => {
  return useContext(walletServiceContext);
};

export const WalletServiceProvider = ({ children }: PropsWithChildren) => {
  const { activePublicKey, activeWalletType, activeWalletName } = useSelector(
    (state: RootState) => state.wallet
  );
  const dispatch = useDispatch();
  const csprClick = useCsprClickService();
  const { networkMajorVersion } = useGetCasperNetwork();
  const { casperNetworkService } = useGetCasperNetworkService();

  const { t } = useTranslation();

  const readOnlyIsConnected = activeWalletType === WalletType.ReadOnly;
  const ledgerIsConnected = activeWalletType === WalletType.Ledger;

  const activeWalletConnected = activeWalletType !== null;

  const signButtonLabel = t('Sign with {{wallet}}', {
    wallet: activeWalletName,
  });

  const walletType = t('the {{wallet}}', { wallet: activeWalletName });

  const getAccountHash = () => {
    if (activePublicKey == null) {
      return null;
    }

    const Account = AccountModel({
      publicKeyHex: activePublicKey,
    });
    return Account.getAccountHash();
  };

  const disconnect = () => {
    csprClick.signOut();
  };

  const connect = () => {
    csprClick.signIn();
  };

  const buyCSPR = () => {
    csprClick.buyCSPR();
  };

  // Keep capability checks stable across renders, but refresh on wallet changes.
  const supportTransactions = useCallback(async () => {
    return csprClick.supportTransactions();
  }, [csprClick.supportTransactions, activeWalletType, activePublicKey]);

  const connectReadOnly = (publicKey: string) => {
    dispatch(
      setActiveWalletAndKey({
        walletType: WalletType.ReadOnly,
        publicKey: publicKey,
      })
    );
  };

  const getTransactionObjByNetworkVersion = (
    transaction: Transaction,
    networkMajorVersion: string
  ) => {
    if (networkMajorVersion === NetworkMajorVersion.V2) {
      return { transaction: { Version1: transaction.toJSON() } };
    } else {
      const deploy = transaction.getDeploy();
      return Deploy.toJSON(deploy);
    }
  };

  const getSignedTransactionByNetworkVersion = (
    transaction: Transaction,
    signature: Uint8Array,
    accountPublicKey: string,
    networkMajorVersion: string
  ) => {
    if (networkMajorVersion === NetworkMajorVersion.V2) {
      const transactionV1 = transaction.getTransactionV1();
      const signedTransactionV1 = TransactionV1.setSignature(
        transactionV1,
        signature,
        CLPublicKey.fromHex(accountPublicKey)
      );
      return Transaction.fromTransactionV1(signedTransactionV1);
    } else {
      const deploy = transaction.getDeploy();
      const signedDeploy = Deploy.setSignature(
        deploy,
        signature,
        CLPublicKey.fromHex(accountPublicKey)
      );
      return Transaction.fromDeploy(signedDeploy);
    }
  };

  const signAndSendTransaction = async (
    transaction: Transaction,
    accountPublicKey: string,
    isDeployV1?: boolean
  ) => {
    const networkVersion = isDeployV1
      ? NetworkMajorVersion.V1
      : networkMajorVersion;
    const transactionJson = getTransactionObjByNetworkVersion(
      transaction,
      networkVersion
    );

    if (activeWalletConnected) {
      return csprClick
        .sign(JSON.stringify(transactionJson), accountPublicKey)
        .then((res: any) => {
          if (res.cancelled) {
            return Promise.reject(WalletServiceError.CANCELLED_SIGN);
          }
          if (res.signature == null) {
            return Promise.reject(res.error);
          }
          if (res.error) {
            return Promise.reject(res.error);
          }

          const signedTransaction = getSignedTransactionByNetworkVersion(
            transaction,
            res.signature,
            accountPublicKey,
            networkVersion
          );

          const isValidSignature = signedTransaction.validate();
          if (!isValidSignature) {
            console.error('Signature could not be verified! ');
          } else {
            try {
              return casperNetworkService.putTransaction(signedTransaction);
            } catch (err: any) {
              console.error('Error: ' + err.message + '\n' + err.data);
            }
          }
        })
        .then((res: PutTransactionResult | PutDeployResult) => {
          return res;
        })
        .catch((error) => {
          Sentry.withScope(function (scope) {
            Sentry.captureMessage('Transaction Flow | Error', {
              level: 'error',
              contexts: {
                'error details': {
                  code: error?.code,
                  error: error?.data,
                },
              },
              tags: {
                functionality: 'Transaction-flow',
                method: 'signAndSendTransaction',
                wallet: csprClick.walletProvider(),
              },
            } as CaptureContext);
          });
          console.error(`Code: ${error?.code}, Message: ${error?.data}`);

          return Promise.reject(error);
        });
    } else {
      return Promise.reject('Cannot sign, no wallet is connected');
    }
  };

  const signTransactionObj = async (
    transaction: Transaction,
    accountPublicKey: string,
    isDeployV1?: boolean
  ) => {
    const networkVersion = isDeployV1
      ? NetworkMajorVersion.V1
      : networkMajorVersion;

    const transactionJson = getTransactionObjByNetworkVersion(
      transaction,
      networkVersion
    );

    if (activeWalletConnected) {
      return csprClick
        .sign(JSON.stringify(transactionJson), accountPublicKey)
        .then((res: any) => {
          if (res === undefined || res.cancelled) {
            return undefined;
          } else if (res.error) {
            Promise.reject(res.error);
            return undefined;
          } else {
            const signedTransaction = getSignedTransactionByNetworkVersion(
              transaction,
              res.signature,
              accountPublicKey,
              networkVersion
            );

            return signedTransaction;
          }
        })
        .catch((error) => {
          Sentry.captureException(error, {
            tags: {
              functionality: 'transaction-flow',
              method: 'signTransactionObj',
              wallet: csprClick.walletProvider(),
            },
          } as ExclusiveEventHintOrCaptureContext);
          console.error(`Code: ${error?.code}, Message: ${error?.data}`);

          return Promise.reject(error);
        });
    } else {
      return Promise.reject('Cannot sign, no wallet is connected');
    }
  };

  const signMessage = (message: string, accountPublicKey: string) => {
    if (activeWalletConnected) {
      return csprClick
        .signMessage(message, accountPublicKey)
        .then((res: any) => {
          if (res.cancelled) {
            return undefined;
          } else {
            return res.signatureHex;
          }
        })
        .catch((error) => {
          Sentry.captureException(error, {
            tags: {
              functionality: 'deploy-flow',
              method: 'signMessage',
              wallet: csprClick.walletProvider(),
            },
          } as ExclusiveEventHintOrCaptureContext);
          console.error(`Code: ${error?.code}, Message: ${error?.data}`);

          return Promise.reject(error);
        });
    } else {
      return Promise.reject('Cannot sign, no wallet is connected');
    }
  };

  const contextProps: WalletService = {
    isSignedIn: activeWalletConnected,
    publicKey: activePublicKey,
    accountHash: getAccountHash(),
    disconnect: disconnect,
    connect: connect,
    buyCSPR: buyCSPR,
    supportTransactions: supportTransactions,
    signTransactionObj: signTransactionObj,
    signAndSendTransaction: signAndSendTransaction,
    signMessage: signMessage,
    activeWalletSignInLabel: signButtonLabel,
    activeWalletTypeLabel: walletType,
    connectReadOnly: connectReadOnly,
    readOnlyIsConnected: readOnlyIsConnected,
    ledgerIsConnected: ledgerIsConnected,
  };

  return (
    <WalletServiceContextProviderV2 value={contextProps} children={children} />
  );
};
